import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ImageProps, { LayerProps, TextLayerData } from "../../../types/imageStudio/images";
import EffectThemeImageGrid from "./ImageList";
import { LayerSidebarContextTarget, LayerSidebarProps } from "../../../types/imageStudio/layerComponent";
import EffectThemeVariationsImageGrid from "./VariationImagesList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faX } from "@fortawesome/free-solid-svg-icons";
import { textLayerToSvgSrc } from "../../../utils/images/imageConvertFormats";
import { ContextMenuLayerSidebar } from "./ContextMenu";
import { ContextProps } from "../../../types/imageStudio/contextMenu";
import { useBootstrapTheme } from "../../../contexts/BootstrapThemeContext";
import { SengiColors } from "../../../utils/SengiColors";

export const LayerSidebar = ({
    layers,
    selectedLayer,
    selectedLayerIDs = [],
    selectedVariationIndex = null,
    onAddVariationAsLayer,
    onChangeVariationAsLayer,
    onSelectedVariationChange,//#
    onSetVariationOnImage,
    onCancelSelection,
    onVariationHover,
    onSelectedLayerChange,
    onSelectedLayerIDsChange,
    onReorderLayers,
    baseImage,
    baseImageVariations=[],
    isBaseImageSelected = false,
    onSelectBaseImage,
    onReplaceBaseImage,
    setLayerHiddenState,
    onClearSidebarItems,
    onDeleteSelectedLayer,
    onDuplicateSelectedLayer,
    onRotateSelectedLayer,
    onCropSelectedLayerSave,
    onFlipHorizontalSelectedLayer,
    onFlipVerticalSelectedLayer,
    onWriteSelectedLayerToBaseImage,
    onMergeSelectedLayer,
    onMergeLayerWithPrevious,
    onResetBaseImage,
    onUndoBaseImage,
    onRedoBaseImage,
    onRotateBaseImage,
    onFlipVerticalBaseImage,
    onFlipHorizontalBaseImage,
    onWriteAllLayerToBaseImage,
    onZoomIn,
    onZoomOut,
    onDeleteVariation,
    onCropBaseImage,
    onChangeLayerToBack,
    onChangeLayerToFront,
    onDownloadAllVariations,
    addBaseImageasLayerAndSetImageAsTransparentBackground
}: LayerSidebarProps) => {
    const { t } = useTranslation("imageStudio");
    const { effectiveTheme } = useBootstrapTheme();
    const [draggedLayerId, setDraggedLayerId] = useState<number | null>(null);
    const [dragOverLayerId, setDragOverLayerId] = useState<number | null>(null);
    const [topPaneHeight, setTopPaneHeight] = useState(320);
    const [isResizingDivider, setIsResizingDivider] = useState(false);
    const [contextTarget, setContextTarget] = useState<LayerSidebarContextTarget | null>(null);

    const sidebarRootRef = useRef<HTMLDivElement | null>(null);
    const resizeStartRef = useRef<{
        startY: number;
        startTopHeight: number;
    } | null>(null);
    const contextMenuLayer = useRef<HTMLDivElement>(null);

    const [contextMenu, setContextMenu] = useState<ContextProps>({
        visible: false,
        x: 0,
        y: 0
    });

    const showVariations = (isBaseImageSelected && baseImageVariations.length > 0) || (selectedLayerIDs.length === 1 && !!selectedLayer);
    const selectedLayerVariations: ImageProps[] = showVariations
        ? (selectedLayer?.variations ?? []).filter((item): item is ImageProps => !!item?.src)
        : [];

    const sortedLayers = useMemo(() => {
        return [...layers].sort(
            (a, b) => Number(a.orderNumber) - Number(b.orderNumber)
        );
    }, [layers]);
    const sidebarItems = useMemo(() => {
        const baseItem = baseImage
            ? [{
                kind: "base" as const,
                key: "base-image",
                orderNumber: 0
            }]
            : [];

        const layerItems = sortedLayers.map((layer) => ({
            kind: "layer" as const,
            key: `layer-${layer.layerID}`,
            orderNumber: Number(layer.orderNumber),
            layer
        }));

        return [...baseItem, ...layerItems];
    }, [baseImage, sortedLayers]);

    const openLayerSidebarContextMenu = (
        e: React.MouseEvent,
        target: LayerSidebarContextTarget
    ) => {
        e.preventDefault();
        e.stopPropagation();

        setContextTarget(target);

        setContextMenu({
            visible: true,
            x: e.clientX,
            y: e.clientY
        });
    };

    const contextLayer = contextTarget?.kind === "layer"
        ? layers.find(layer => layer.layerID === contextTarget.layerID) ?? null
        : null;

    const isLayerSelected = (layer: LayerProps) => {
        if (selectedLayerIDs.length > 0) {
            return selectedLayerIDs.includes(layer.layerID);
        }
        return selectedLayer?.layerID === layer.layerID;
    };
    const isBaseSelected = () => {
        return isBaseImageSelected || (!selectedLayer && selectedLayerIDs.length === 0);
    };

    const handleSelectLayer = (layer: LayerProps) => {
        if (layer.hidden) return;

        onVariationHover?.(-1, null);
        onSelectedVariationChange?.(-1, null);
        onCancelSelection?.();

        onSelectedLayerChange?.(layer);
        onSelectedLayerIDsChange?.([layer.layerID]);
    };

    const handleDragStart = (layerId: number) => {
        setDraggedLayerId(layerId);
    };

    const handleDragEnd = () => {
        setDraggedLayerId(null);
        setDragOverLayerId(null);
    };

    const handleDrop = (targetLayerId: number) => {
        if (draggedLayerId == null || draggedLayerId === targetLayerId) {
            handleDragEnd();
            return;
        }

        const current = [...sortedLayers];
        const fromIndex = current.findIndex((layer) => layer.layerID === draggedLayerId);
        const toIndex = current.findIndex((layer) => layer.layerID === targetLayerId);

        if (fromIndex === -1 || toIndex === -1) {
            handleDragEnd();
            return;
        }

        const [movedLayer] = current.splice(fromIndex, 1);
        current.splice(toIndex, 0, movedLayer);

        const reordered = current.map((layer, index) => ({
            ...layer,
            orderNumber: String(index + 1)
        }));

        onReorderLayers?.(reordered);
        handleDragEnd();
    };
    const clearSidebarItems = () => {
        onVariationHover?.(-1, null);
        onSelectedVariationChange?.(-1, null);
        onCancelSelection?.();
        onClearSidebarItems();
    };
    
    const toLayerThumbnailImage = (layer: LayerProps): ImageProps => {
        let src = "";

        if (layer.type === "image" || layer.type === "ai-variation") {
            src = layer.imageEdit?.src || layer.imageData?.src || "";
        } else if (layer.type === "drawings") {
            src = layer.drawingData?.rasterSrc || "";
        } else if (layer.type === "text" && layer.textData) {
            src = textLayerToSvgSrc(layer.width,layer.height,layer.textData)
        }
        if (!src) {
            const svg = `
                <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50">
                    <rect width="100%" height="100%" fill="#2b2b2b"/>
                    <text
                        x="50%"
                        y="50%"
                        dominant-baseline="middle"
                        text-anchor="middle"
                        fill="#ffffff"
                        font-size="9"
                        font-family="Arial"
                    >
                        ${layer.type}
                    </text>
                </svg>
            `;
            src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
        }

        return {
            file: null,
            src,
            width: 50,
            height: 50,
            timeOfUse: new Date(),
            title: `${layer.type} ${layer.orderNumber}`,
            alt: `${layer.type}-${layer.layerID}`,
            onClick: () => handleSelectLayer(layer)
        };
    };
    const toBaseImageThumbnail = (img: ImageProps): ImageProps => ({
        ...img,
        width: 50,
        height: 50,
        title: "Base Image",
        alt: "base-image",
        onClick: () => onSelectBaseImage?.()
    });
    const handleDividerMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        resizeStartRef.current = {
            startY: e.clientY,
            startTopHeight: topPaneHeight
        };

        setIsResizingDivider(true);
    };

    useMemo(() => {
        return undefined;
    }, []);
    useEffect(() => {
        if (!isResizingDivider) return;

        const handleMouseMove = (e: MouseEvent) => {
            const sidebarRoot = sidebarRootRef.current;
            const start = resizeStartRef.current;

            if (!sidebarRoot || !start) return;

            const rect = sidebarRoot.getBoundingClientRect();

            const minTop = 120;
            const minBottom = 140;
            const dividerSpace = 24;

            const maxTop = Math.max(
                minTop,
                rect.height - minBottom - dividerSpace
            );

            const deltaY = e.clientY - start.startY;
            const nextTopHeight = start.startTopHeight + deltaY;

            setTopPaneHeight(
                Math.max(minTop, Math.min(maxTop, nextTopHeight))
            );
        };

        const handleMouseUp = () => {
            resizeStartRef.current = null;
            setIsResizingDivider(false);
        };

        document.body.style.cursor = "row-resize";
        document.body.style.userSelect = "none";

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            document.body.style.cursor = "";
            document.body.style.userSelect = "";

            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isResizingDivider]);
    useEffect(()=>{
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                setContextMenu(prev => ({ ...prev, visible: false }));
                return;
            }
        };
        window.addEventListener("keydown", onKey);

        return () => {
            window.removeEventListener("keydown", onKey);
        };
    },[]);

    return (
        <div
            ref={sidebarRootRef}
            id="layer-sidebar-root"
            className="d-flex flex-column h-100 w-100 p-2"
            style={{
                minHeight: 0,
                overflow: "hidden"
            }}
        >
            <div
                style={{
                    height: showVariations ? `${topPaneHeight}px` : "100%",
                    flex: showVariations ? `0 0 ${topPaneHeight}px` : "1 1 auto",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden"
                }}
            >
                {baseImage || layers.length > 0
                    ?   <div className="row mb-1">
                            <div className="col fw-bold my-auto"
                                style={{
                                    fontSize: 16,
                                }}
                            >
                                {t('common.layer')}
                            </div>
                            <div className="col-auto">
                                <button
                                    className="border-0 btn"
                                    title={t("layer.clearAll")}
                                    onClick={() => clearSidebarItems()}
                                >
                                    <FontAwesomeIcon icon={faX} />
                                </button>
                            </div>
                            <ContextMenuLayerSidebar
                                layerOrderNumber={contextLayer?.orderNumber ?? null}
                                layerID={contextLayer?.layerID ?? null}
                                layerType={contextLayer?.type ?? null}
                                allLayer={layers}
                                wrapperRef={sidebarRootRef}
                                contextMenu={contextMenu}
                                setContextMenu={setContextMenu}

                                baseImage={contextTarget?.kind === "base" ? baseImage ?? null : null}
                                onResetBaseImage={onResetBaseImage}
                                onUndoBaseImage={onUndoBaseImage}
                                onRedoBaseImage={onRedoBaseImage}
                                onRotateBaseImage={onRotateBaseImage}
                                onFlipVerticalBaseImage={onFlipVerticalBaseImage}
                                onFlipHorizontalBaseImage={onFlipHorizontalBaseImage}
                                onWriteAllLayerToBaseImage={onWriteAllLayerToBaseImage}
                                onZoomIn={onZoomIn}
                                onZoomOut={onZoomOut}
                                onCropBaseImage={onCropBaseImage}

                                selectedLayerIDs={selectedLayerIDs}
                                rotateList={[90, 180, 270]}
                                onDeleteSelectedLayer={onDeleteSelectedLayer}
                                onDuplicateSelectedLayer={onDuplicateSelectedLayer}
                                addBaseImageasLayerAndSetImageAsTransparentBackground={addBaseImageasLayerAndSetImageAsTransparentBackground}
                                onRotateSelectedLayer={(degree: number, layerID: number) => onRotateSelectedLayer?.(degree, layerID)}
                                onCropSelectedLayerSave={onCropSelectedLayerSave}
                                onFlipHorizontalSelectedLayer={onFlipHorizontalSelectedLayer}
                                onFlipVerticalSelectedLayer={onFlipVerticalSelectedLayer}
                                onWriteSelectedLayerToBaseImage={onWriteSelectedLayerToBaseImage}
                                onMergeSelectedLayer={onMergeSelectedLayer}
                                onMergeLayerWithPrevious={onMergeLayerWithPrevious}
                                onHideSelectedLayer={setLayerHiddenState}
                                onChangeLayerToBack={onChangeLayerToBack}
                                onChangeLayerToFront={onChangeLayerToFront}
                            />
                        </div>
                    : <></>
                }

                <div
                    className="d-flex flex-column gap-1"
                    style={{
                        overflowY: "auto",
                        minHeight: 0,
                        paddingRight: 4
                    }}
                    ref={contextMenuLayer}
                >
                    {sidebarItems.map((item, index) => {
                        if (item.kind === "base") {
                            if (!baseImage) return null;

                            const selected = isBaseSelected();

                            return (
                                <div
                                    key={item.key}
                                    onClick={() => {
                                        onVariationHover?.(-1, null);
                                        onSelectedVariationChange?.(-1, null);
                                        onCancelSelection?.();
                                        onSelectBaseImage?.();
                                    }}
                                    onContextMenu={(e) => openLayerSidebarContextMenu(e, { kind: "base" })}
                                    className="row g-0 align-items-center p-2"
                                    style={{
                                        cursor: "pointer",
                                        userSelect: "none",
                                        borderRadius: 8,
                                        border: selected
                                            ? "2px solid #9381FF"
                                            : "1px solid rgba(255,255,255,0.18)",
                                        background: selected
                                            ? "rgba(147,129,255,0.12)"
                                            : "rgba(255,255,255,0.04)",
                                        flex: "0 0 auto"
                                    }}
                                >
                                    <div className="col-auto">
                                        <EffectThemeImageGrid
                                            size={"50px"}
                                            images={[toBaseImageThumbnail(baseImage)]}
                                            objectFit="contain"
                                            backgroundColor="rgba(0,0,0,0.35)"
                                        />
                                    </div>

                                    <div className="col ms-2 overflow-hidden">
                                        <div
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: effectiveTheme === 'dark' ? SengiColors.sng_pearl : SengiColors.sng_anthracite,
                                                textTransform: "capitalize"
                                            }}
                                        >
                                            {t('common.base')}
                                        </div>

                                        <div
                                            style={{
                                                fontSize: 11,
                                                color: effectiveTheme === 'dark' ? SengiColors.sng_silver : SengiColors.sng_disabled,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis"
                                            }}
                                        >
                                            # 0
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        const layer = item.layer;
                        const selected = isLayerSelected(layer);
                        const disabled = item.layer.hidden;
                        const dragOver = dragOverLayerId === layer.layerID;

                        return (
                            <div
                                key={layer.layerID}
                                draggable
                                onDragStart={() => handleDragStart(layer.layerID)}
                                onDragEnd={handleDragEnd}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setDragOverLayerId(layer.layerID);
                                }}
                                onDragLeave={() => {
                                    if (dragOverLayerId === layer.layerID) {
                                        setDragOverLayerId(null);
                                    }
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    handleDrop(layer.layerID);
                                }}
                                onClick={() => handleSelectLayer(layer)}
                                onContextMenu={(e) =>
                                    openLayerSidebarContextMenu(e, {
                                        kind: "layer",
                                        layerID: layer.layerID
                                    })
                                }
                                className="row g-0 align-items-center p-2"
                                style={{
                                    cursor: "pointer",
                                    userSelect: "none",
                                    borderRadius: 8,
                                    border: selected &&! disabled
                                        ? "2px solid #9381FF"
                                        : dragOver
                                            ? "2px dashed #9381FF"
                                            :"1px solid rgba(255,255,255,0.18)",
                                    background: selected
                                        ? "rgba(147,129,255,0.12)"
                                        : disabled
                                            ? "1px solid rgba(255,255,255,0.1)"
                                            : "rgba(255,255,255,0.04)",
                                    opacity: disabled ? 0.45 : 1,
                                    flex: "0 0 auto"
                                }}
                            >
                                <div className="col-auto">
                                    <EffectThemeImageGrid
                                        size={"50px"}
                                        gap={0}
                                        images={[toLayerThumbnailImage(layer)]}
                                        backgroundColor="rgba(0,0,0,0.35)"
                                        objectFit="contain"
                                    />
                                </div>

                                <div className="col ms-2 overflow-hidden">
                                    <div
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: effectiveTheme === 'dark' ? SengiColors.sng_pearl : SengiColors.sng_anthracite,
                                            textTransform: "capitalize"
                                        }}
                                    >
                                        {t(`common.layertype.${layer.type}`)}
                                    </div>

                                    <div
                                        style={{
                                            fontSize: 11,
                                            color: effectiveTheme === 'dark' ? SengiColors.sng_silver : SengiColors.sng_disabled,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis"
                                        }}
                                    >
                                        # {layer.orderNumber}
                                    </div>
                                </div>
                                <div className="col-auto ms-2 overflow-hidden">
                                    <button
                                        type="button"
                                        className="border-0 bg-transparent p-1"
                                        draggable={false}
                                        title={layer.hidden ? "Show layer" : "Hide layer"}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();

                                            setLayerHiddenState(layer.layerID, !layer.hidden);
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={layer.hidden ? faEyeSlash : faEye}
                                        />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {showVariations && (
                <>
                    <div
                        onMouseDown={handleDividerMouseDown}
                        style={{
                            height: 16,
                            cursor: "row-resize",
                            margin: "6px 0",
                            position: "relative",
                            flex: "0 0 16px",
                            zIndex: 5,
                            touchAction: "none",
                            userSelect: "none"
                        }}
                    >
                        <div
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: 0,
                                right: 0,
                                transform: "translateY(-50%)",
                                height: 2,
                                background: "#6c757d",
                                borderRadius: 2
                            }}
                        />
                    </div>

                    <div
                        style={{
                            minHeight: 0,
                            flex: "1 1 auto",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden"
                        }}
                    >
                        <div className="mb-1 fw-bold"
                            style={{
                                fontSize: 16,
                            }}
                        >
                            {t('common.variations')}
                        </div>

                        <div
                            style={{
                                overflowY: "auto",
                                minHeight: 0,
                                paddingRight: 4
                            }}
                        >
                            <EffectThemeVariationsImageGrid
                                gap={2}
                                width="48%"
                                images={isBaseImageSelected ? baseImageVariations : selectedLayerVariations}
                                isBaseImageSelected={isBaseImageSelected}
                                className="pt-1"
                                selectedImageIndex={selectedVariationIndex ?? undefined}
                                onImageHover={(index, img) => onVariationHover?.(index, img)}
                                onImageHoverEnd={() => onVariationHover?.(-1, null)}
                                onImageSelect={(index, img) => onSelectedVariationChange?.(index, img)}
                                onChangeVariationAsLayer={onChangeVariationAsLayer}
                                onAddVariationAsLayer={onAddVariationAsLayer}
                                onCancelSelection={onCancelSelection}
                                onSetVariationOnImage={onSetVariationOnImage}
                                selectedLayer={selectedLayer}
                                onReplaceBaseImage={onReplaceBaseImage}
                                baseImage={baseImage ?? null}
                                onDeleteVariation={onDeleteVariation}
                                onDownloadAllVariations={onDownloadAllVariations}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};